import React, { useState } from 'react';
import { Upload, FileText, ArrowRight, CloudUpload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import FileUploader from './FileUploader';
import { useAuth } from '../hooks/useAuth';
import { uploadInventoryToCloud } from '../utils/supabaseSync';
import { aiAnalyst } from '../utils/aiAnalyst';

const WelcomeScreen = ({ onDataLoaded }) => {
    const { user } = useAuth();
    const isAdmin = user?.email?.includes('admin'); // Simple check for now
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error'

    // פונקציה שמטפלת בנתונים אחרי שהקובץ נטען מקומית (ע"י FileUploader)
    const handleFileProcessed = async (data) => {
        // אם מנהל - מעלים לענן
        if (isAdmin) {
            setIsUploading(true);
            setUploadStatus(null);

            try {
                const result = await uploadInventoryToCloud(data, (progress) => {
                    setUploadProgress(progress);
                });

                if (result.success) {
                    setUploadStatus('success');
                    // אחרי העלאה מוצלחת, טוענים לאפליקציה כרגיל
                    setTimeout(() => {
                        onDataLoaded(data);
                    }, 1500);
                } else {
                    setUploadStatus('error');
                    console.error(result.error);
                }
            } catch (err) {
                setUploadStatus('error');
            } finally {
                setIsUploading(false);
            }
        } else {
            // משתמש רגיל - טעינה מקומית רגילה (בינתיים, עד שיהיה useInventory)
            onDataLoaded(data);
        }
    };

    return (
        <div className="min-h-[100dvh] bg-slate-50 flex flex-col items-center justify-center p-4 relative overflow-y-auto md:overflow-hidden" dir="rtl">
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-white -z-10"></div>

            <div className="max-w-2xl w-full text-center space-y-6 relative z-10 p-8 glass-panel rounded-3xl animate-fade-in-up">

                {/* Header */}
                <div className="flex flex-col items-center justify-center">
                    <div className="bg-slate-900 text-white w-16 h-16 rounded-2xl flex items-center justify-center font-black text-3xl shadow-xl shadow-slate-900/20 mb-4">
                        A
                    </div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        {isAdmin ? 'עדכון מלאי ראשי' : 'מערכת ניהול מלאי'}
                    </h1>
                    <p className="text-slate-500 mt-2">
                        {isAdmin
                            ? 'העלאת קובץ Excel לעדכון בסיס הנתונים בענן'
                            : 'טעינת נתונים לצפייה וניתוח'
                        }
                    </p>
                </div>

                {/* Upload Section */}
                <div className="py-6">
                    {isAdmin && (
                        <div className="mb-4 flex items-center justify-center gap-2 text-aeg-blue font-medium bg-blue-50 py-2 px-4 rounded-full inline-flex mx-auto">
                            <CloudUpload size={20} />
                            <span>מצב מנהל: סנכרון לענן פעיל</span>
                        </div>
                    )}

                    {isUploading ? (
                        <div className="py-12 flex flex-col items-center justify-center space-y-4">
                            <Loader2 className="w-12 h-12 text-aeg-blue animate-spin" />
                            <div className="text-slate-600 font-medium">מעלה נתונים לענן... {uploadProgress}%</div>
                            <div className="w-64 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-aeg-blue transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        </div>
                    ) : uploadStatus === 'success' ? (
                        <div className="py-10 flex flex-col items-center justify-center text-emerald-600 animate-fade-in">
                            <CheckCircle size={48} className="mb-4" />
                            <h3 className="text-xl font-bold">העלאה הושלמה בהצלחה!</h3>
                            <p className="text-emerald-600/80">הנתונים סונכרנו לבסיס הנתונים</p>
                        </div>
                    ) : (
                        <div className="relative group">
                            <FileUploader onDataLoaded={handleFileProcessed} />
                        </div>
                    )}
                </div>

                {/* Footer Info */}
                <div className="border-t border-slate-100 pt-6 flex justify-between text-xs text-slate-400">
                    <span>משתמש מחובר: {user?.email}</span>
                    <span>v2.0 (Cloud Enabled)</span>
                </div>
            </div>
        </div>
    );
};

export default WelcomeScreen;
